import { Modal as AntModal, ConfigProvider } from 'antd';
import { Colors } from 'const/colors';
import { ReactNode } from 'react';

export interface ModalProps {
  open: boolean;
  onCancel: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  centered?: boolean;
  closable?: boolean;
  maskClosable?: boolean;
  keyboard?: boolean;
  width?: number | string;
  destroyOnHidden?: boolean;
  rootClassName?: string;
}

/**
 * Base Modal Component using Ant Design's Modal.
 *
 * @param props - ModalProps for configuring the modal.
 * @returns A styled modal dialog.
 */
export function Modal({
  open,
  onCancel,
  title,
  children,
  footer,
  className = '',
  centered = true,
  closable = false,
  maskClosable = true,
  keyboard = true,
  width,
  destroyOnHidden,
  rootClassName,
}: ModalProps) {
  return (
    <AntModal
      open={open}
      onCancel={onCancel}
      rootClassName={rootClassName}
      className={className}
      centered={centered}
      closable={closable}
      title={title}
      footer={footer}
      maskClosable={maskClosable}
      keyboard={keyboard}
      width={width}
      destroyOnHidden={destroyOnHidden}
    >
      {children}
    </AntModal>
  );
}

/**
 * Modal component with a blue header and white title text.
 *
 * @param props - ModalProps for configuring the modal.
 * @returns A modal dialog with blue header styling.
 */
export function BlueModal(props: ModalProps) {
  return (
    <ConfigProvider
      theme={{
        components: {
          Modal: {
            headerBg: Colors.BleuHellio,
            titleColor: Colors.white,
          },
        },
      }}
    >
      <Modal {...props} />
    </ConfigProvider>
  );
}

/**
 * Modal component with a white header and blue title text.
 *
 * @param props - ModalProps for configuring the modal.
 * @returns A modal dialog with white header styling.
 */
export function WhiteModal({
  open,
  onCancel,
  title,
  children,
  footer,
  className = '',
  centered = true,
  closable = false,
  maskClosable = true,
  keyboard = true,
  width,
  destroyOnHidden,
  rootClassName,
}: ModalProps) {
  return (
    <ConfigProvider
      theme={{
        components: {
          Modal: {
            headerBg: Colors.white,
            titleColor: Colors.BleuHellio,
            contentBg: Colors.white,
          },
        },
      }}
    >
      <AntModal
        open={open}
        onCancel={onCancel}
        rootClassName={rootClassName}
        className={`white-modal [&_.ant-modal-container]:rounded-2xl [&_.ant-modal-container]:overflow-hidden ${className}`}
        centered={centered}
        closable={closable}
        maskClosable={maskClosable}
        keyboard={keyboard}
        title={title}
        footer={footer}
        width={width}
        destroyOnHidden={destroyOnHidden}
      >
        {children}
      </AntModal>
    </ConfigProvider>
  );
}

export interface SmallModalProps extends Omit<ModalProps, 'title'> {
  title?: ReactNode;
  zIndex?: number;
}

/**
 * Compact modal variant with fixed small width and rounded body.
 *
 * @param props - SmallModalProps for configuring the modal.
 * @returns A compact modal dialog.
 */
export function SmallModal({
  open,
  onCancel,
  title = null,
  children,
  footer = null,
  className = '',
  centered = true,
  closable = true,
  maskClosable = true,
  keyboard = true,
  zIndex,
}: SmallModalProps) {
  const baseClassName = '[&_.ant-modal-body]:!rounded-t-xl [&&]:!w-[520px]';

  return (
    <AntModal
      open={open}
      onCancel={onCancel}
      className={`${baseClassName} ${className}`.trim()}
      centered={centered}
      closable={closable}
      title={title}
      footer={footer}
      maskClosable={maskClosable}
      keyboard={keyboard}
      zIndex={zIndex}
    >
      {children}
    </AntModal>
  );
}

/**
 * Full-width modal aligned to the top, filling the viewport height.
 * Uses white background and blue title (inherits WhiteModal styling).
 */
export function WideModal(props: Omit<ModalProps, 'width'>) {
  return (
    <WhiteModal
      {...props}
      closable
      centered
      className={`wide-modal [&_.ant-modal-container]:rounded-2xl [&_.ant-modal-body]:flex [&_.ant-modal-body]:overflow-auto [&_.ant-modal-body>*]:max-w-3xl [&_.ant-modal-body>*]:mx-auto [&_.content]:pt-12 ${props.className || ''}`}
    />
  );
}

export interface MultiStepModalProps extends Omit<
  ModalProps,
  'title' | 'footer'
> {
  width?: number;
  destroyOnHidden?: boolean;
  wrapClassName?: string;
}
